import { useContext, useMemo } from 'react';

import { type ProgressProps, Progress as AntdProgress } from 'antd';

import { ThemeContext } from '@/components/globals/ThemeProvider';
import useStepStore from '@/stores/steps';

const Progress = () => {
  const { theme } = useContext(ThemeContext);
  const colors = useMemo(() => {
    if (theme === 'dark') {
      return {
        stroke: {
          '0%': '#FDBA00',
          '100%': '#FFDC52'
        } as ProgressProps['strokeColor'],
        trail: '#3F3F46'
      };
    }

    return {
      stroke: {
        '0%': '#0D2772',
        '100%': '#194AD8'
      } as ProgressProps['strokeColor'],
      trail: '#F0F0F0'
    };
  }, [theme]);

  const progress = useStepStore(({ step, steps, stepData }) => {
    if (stepData?.progress) {
      return stepData.progress;
    }

    const stepsWithProgress = steps.map((item, index) => ({ ...item, index })).filter(s => s.progress !== undefined);
    const currentRange = stepsWithProgress.reduce(
      (range, stepWithProgress) => {
        if (stepWithProgress.index <= step && stepWithProgress.index >= range.min.index) {
          range.min = {
            index: stepWithProgress.index,
            progress: stepWithProgress.progress!
          };
        }

        if (stepWithProgress.index >= step && stepWithProgress.index <= range.max.index) {
          range.max = {
            index: stepWithProgress.index,
            progress: stepWithProgress.progress!
          };
        }

        return range;
      },
      { min: { index: 0, progress: 0 }, max: { index: steps.length - 1, progress: 100 } }
    );

    const diferenceBetweenIndex = currentRange.max.index - currentRange.min.index;
    const diferenceBetweenProgress = currentRange.max.progress - currentRange.min.progress;
    const currentStepOffset = step - currentRange.min.index;

    return currentRange.min.progress + (currentStepOffset * diferenceBetweenProgress) / diferenceBetweenIndex;
  });

  return (
    <AntdProgress
      showInfo={false}
      className='leading-0! **:rounded-none!'
      percent={progress}
      size={{ height: 6 }}
      strokeColor={colors.stroke}
      trailColor={colors.trail}
    />
  );
};

export default Progress;
